
import { computed, defineComponent, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module compponents
import StepCompany from '@/modules/CreateOrderModule/components/StepCompany/index.vue'
import StepSenders from '@/modules/CreateOrderModule/components/StepSenders/index.vue'
import StepTimeSlots from '@/modules/CreateOrderModule/components/StepTimeSlots/index.vue'
import StepDeliveryTimeSlots from '@/modules/CreateOrderModule/components/StepDeliveryTimeSlots/index.vue'
import StepDate from '@/modules/CreateOrderModule/components/StepDateReactive/index.vue'
import StepReceivers from '@/modules/CreateOrderModule/components/StepReceivers/index.vue'
import StepCargo from '@/modules/CreateOrderModule/components/StepCargo/index.vue'
import CompletedCargos from '@/modules/CreateOrderModule/components/CompletedCargos/index.vue'
import StepCreate from '@/modules/CreateOrderModule/components/StepCreate/index.vue'
import NewAdditionalServices from '@/modules/CreateOrderModule/components/NewAdditionalServices/index.vue'
import ExcelMultipleModal from '@/modules/CreateOrderModule/components/ExcelMultipleModal/index.vue'

// types
import { ReceiverT } from '@/core/types/receivers.types'
import { CargoT, CompletedCargoT } from '@/core/types/create_order.types'
import { CompanyT, CompanySetting } from '@/core/types/companies.types'

// composable
import useCreateOrder from '@/modules/CreateOrderModule/composable/useCreateOrder'
import useNotifications from '@/common/composable/useNotifications'
import { useRouter } from 'vue-router'
import { useScroll } from '@/common/composable/useScroll'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { companies } from '@/core/realisations/companies.realisation'

import { SenderT } from '@/core/types/senders.types'
import { AdditionalServicesI } from '@/core/types/additional_services.types'

// constants
import { LTLHintText } from '@/core/constants/orders.constants'
import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'
import { AdditionalByIdT } from '@/core/types/billing.types'
import { billing } from '@/core/realisations/billing.realisation'
import { TimeSlotForCreateOrder } from '@/new-services/types/time-slots'

const CASH_SETTING_ID = 29
const KASPI_SETTING_ID = 30
const BOSCH_ID = 914
const SHIPMENT_EXPRESS_3_HOURS_SETTING_ID = 40
const SHIPMENT_EXPRESS_DAY_TO_DAY_SETTING_ID = 41

const TIME_SLOTS_SETTING_ID = 32

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        //
        'step-company': StepCompany,
        'step-senders': StepSenders,
        'step-time-slots': StepTimeSlots,
        'step-delivery-time-slots': StepDeliveryTimeSlots,
        'step-date': StepDate,
        'step-receivers': StepReceivers,
        'step-cargo': StepCargo,
        'completed-cargos': CompletedCargos,
        'step-create': StepCreate,
        'new-additional-services': NewAdditionalServices,
        'excel-multiple-modal': ExcelMultipleModal,
    },
    setup() {
        const router = useRouter()
        const { scrollTo } = useScroll()

        const state = reactive({
            company_id: null as number | null,
            company_verify: null as number | null,
            is_billing_loading: false,
            additional_services: [] as AdditionalByIdT[],
            // sender
            sender_id: null as number | null,
            sender_cash_enabled: false,
            sender_kaspi_enabled: false,
            sender_shipment_type_3_hour_enabled: false,
            sender_shipment_type_day_to_day_enabled: false,
            sender_city_id: null as number | null,
            sender_dispatcher_sector_id: null as number | null,
            sender_title: null as string | null,
            //
            sender_additional_services: { ...EMPTY_ADDITIONAL_SERVICES },
            is_sender_additional_services_setted: false,
            is_sender_additional_services_update: false,
            // date
            send_date: null as string | null,
            send_period: null as number | null,
            // receiver
            receiver: null as ReceiverT | null,
            // cargos
            completed_cargos: [] as CompletedCargoT[],
            // update
            is_updating_cargo: false,
            updating_cargo_index: null as number | null,
            updating_cargo: null as CompletedCargoT | null,
            // create order
            is_creating_order: false,
            is_order_created: false,
            is_excel_loading: false,
        })

        const take_time_slot_state = reactive({
            is_enabled: false,
            is_completed: false,
            sender: null as SenderT | null,
            timeSlot: null as TimeSlotForCreateOrder | null
        })

        const delivery_time_slot_state = reactive({
            is_component_showed: false,
            cargo_index: null as number | null,
        })

        const excel_multiple_state = reactive({
            is_opened: false,
        })

        const loadBilling = async () => {
            try {
                state.is_billing_loading = true

                const response = await billing.getTariffThirdByCompanyId({
                    companyId: state.company_id as number,
                })

                state.additional_services = response.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_billing_loading = false
            }
        }

        // companies actions
        const onCompanySubmit = (company_id: number, company: CompanyT) => {
            state.company_id = company_id
            state.company_verify = company.verify

            if (company.billing_enabled) {
                loadBilling()
            }
        }

        const onCompanyReset = () => {
            state.company_id = null
            state.company_verify = null
            state.sender_id = null
            state.sender_cash_enabled = false
            state.sender_kaspi_enabled = false
            state.sender_shipment_type_3_hour_enabled = false
            state.sender_shipment_type_day_to_day_enabled = false
            state.sender_additional_services = { ...EMPTY_ADDITIONAL_SERVICES }
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = false
            state.sender_city_id = null
            state.sender_dispatcher_sector_id = null
            state.completed_cargos = []

            take_time_slot_state.is_enabled = false
            take_time_slot_state.is_completed = false
            take_time_slot_state.sender = null
            take_time_slot_state.timeSlot = null

            delivery_time_slot_state.is_component_showed = false
            delivery_time_slot_state.cargo_index = 0
        }

        const onTakeTimeSlotSubmit = () => {
            take_time_slot_state.is_completed = true
        }

        const onTakeTimeSlotReset = () => {
            take_time_slot_state.is_completed = false
        }

        // senders actions
        const onSenderSubmit = (sender: SenderT & { dispatcher_sector_id: number; company_settings: {settingId: number; isActive: boolean}[]; cityType: number; express_available: boolean; }) => {
            state.sender_id = sender.id
            state.sender_city_id = sender.city_id
            state.sender_dispatcher_sector_id = sender.dispatcher_sector_id
            state.sender_title = sender.title

            const company_setting_ids = sender.company_settings.filter((setting) => setting.isActive).map((setting) => setting.settingId)

            state.sender_cash_enabled = company_setting_ids.includes(CASH_SETTING_ID)
            state.sender_kaspi_enabled = company_setting_ids.includes(KASPI_SETTING_ID)
            state.sender_shipment_type_3_hour_enabled = company_setting_ids.includes(SHIPMENT_EXPRESS_3_HOURS_SETTING_ID)

            if (sender.express_available) {
                state.sender_shipment_type_day_to_day_enabled = company_setting_ids.includes(SHIPMENT_EXPRESS_DAY_TO_DAY_SETTING_ID)
            }

            if (sender.cityType && sender.cityType !== 3) {
                take_time_slot_state.is_enabled = company_setting_ids.includes(TIME_SLOTS_SETTING_ID)

                if (take_time_slot_state.is_enabled) {
                    take_time_slot_state.sender = { ...sender }
                }
            }
        }

        const onSenderReset = () => {
            state.sender_id = null
            state.sender_cash_enabled = false
            state.sender_kaspi_enabled = false
            state.sender_shipment_type_3_hour_enabled = false
            state.sender_shipment_type_day_to_day_enabled = false
            state.sender_city_id = null
            state.sender_dispatcher_sector_id = null
            state.sender_title = null
            state.sender_additional_services = { ...EMPTY_ADDITIONAL_SERVICES }
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = false
            state.completed_cargos = []

            state.send_date = null
            state.send_period = null

            take_time_slot_state.is_enabled = false
            take_time_slot_state.is_completed = false
            take_time_slot_state.sender = null
            take_time_slot_state.timeSlot = null

            delivery_time_slot_state.is_component_showed = false
            delivery_time_slot_state.cargo_index = 0
        }

        // sender additional services actions
        const onSubmitSenderAdditionalServices = (additional_services: AdditionalServicesI) => {
            state.sender_additional_services = { ...additional_services }
            state.is_sender_additional_services_setted = true
        }

        const onStartUpdateSenderAdditionalServices = () => {
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = true
        }

        // date actions
        const onDateSubmit = ({ date, period_id }: { date: string; period_id: number }) => {
            state.send_date = date
            state.send_period = period_id

            const today = new Date()
            const todayStr = today.toISOString().slice(0, 10)

            if (date === todayStr) {
                take_time_slot_state.is_enabled = false
                take_time_slot_state.is_completed = false
                take_time_slot_state.sender = null
                take_time_slot_state.timeSlot = null
            }
        }

        const onDateReset = () => {
            state.send_date = null
            state.send_period = null

            take_time_slot_state.is_completed = false
            take_time_slot_state.sender = null
            take_time_slot_state.timeSlot = null
        }

        // receiver actions
        const onReceiverSubmit = (receiver: ReceiverT) => {
            state.receiver = receiver
        }

        const onReceiverReset = () => {
            state.receiver = null
            state.completed_cargos = []

            delivery_time_slot_state.is_component_showed = false
            delivery_time_slot_state.cargo_index = 0
        }

        // cargo actions
        const cancelUpdateCargo = () => {
            state.is_updating_cargo = false
            state.updating_cargo_index = null
            state.updating_cargo = null

            state.receiver = null
        }

        const onCargoSubmit = (cargo: CargoT) => {
            if (!state.receiver) return

            if (state.is_updating_cargo) {
                if (state.updating_cargo_index === null) return

                state.completed_cargos[state.updating_cargo_index].receiver = state.receiver
                state.completed_cargos[state.updating_cargo_index].logistics_info = cargo

                cancelUpdateCargo()
                return
            }

            const completed_cargo: CompletedCargoT = {
                receiver: state.receiver,
                logistics_info: cargo,
                deliveryTimeSlot: null
            }

            state.completed_cargos.unshift(completed_cargo)
            state.receiver = null

            if (take_time_slot_state.is_enabled) {
                if (completed_cargo.receiver.city_type && completed_cargo.receiver.city_type === 3) {
                    return
                }

                delivery_time_slot_state.is_component_showed = true
                delivery_time_slot_state.cargo_index = 0
            }
        }

        const onDeleteCargo = (completed_cargo_index: number) => {
            state.completed_cargos.splice(completed_cargo_index, 1)
        }

        const onStartUpdateCargo = (completed_cargo_index: number) => {
            const completed_cargo = state.completed_cargos[completed_cargo_index]
            if (!completed_cargo) return

            scrollTo('.StepCargo')

            state.updating_cargo_index = completed_cargo_index
            state.updating_cargo = completed_cargo
            state.is_updating_cargo = true

            state.receiver = completed_cargo.receiver

            if (completed_cargo.deliveryTimeSlot) {
                delivery_time_slot_state.cargo_index = completed_cargo_index
                delivery_time_slot_state.is_component_showed = true
            }
        }

        const onDeliveryTimeSlotSubmit = (payload: { templateId: number; timeFromTo: string; deliveryDate: string; }) => {
            if (state.completed_cargos[delivery_time_slot_state.cargo_index as number]) {
                state.completed_cargos[delivery_time_slot_state.cargo_index as number].deliveryTimeSlot = payload
                
                delivery_time_slot_state.is_component_showed = false
                delivery_time_slot_state.cargo_index = null
            }
        }

        const onUpdateCompletedCargoAdditionalServices = ({
            index,
            additional_services,
        }: {
            index: number
            additional_services: AdditionalServicesI
        }) => {
            state.completed_cargos[index].logistics_info.additional_services.hasRisingToTheFloor =
                additional_services.hasRisingToTheFloor
            state.completed_cargos[index].logistics_info.additional_services.hasManipulator =
                additional_services.hasManipulator
            state.completed_cargos[index].logistics_info.additional_services.hasCrane =
                additional_services.hasCrane
            state.completed_cargos[index].logistics_info.additional_services.hasHydraulicTrolley =
                additional_services.hasHydraulicTrolley
            state.completed_cargos[index].logistics_info.additional_services.hasLoader =
                additional_services.hasLoader
        }

        const resetOrder = () => {
            state.is_order_created = false

            state.updating_cargo = null
            state.updating_cargo_index = null
            state.is_updating_cargo = false

            state.completed_cargos = []

            state.receiver = null

            state.send_date = null
            state.send_period = null

            state.sender_id = null
            state.sender_cash_enabled = false
            state.sender_kaspi_enabled = false
            state.sender_shipment_type_3_hour_enabled = false
            state.sender_shipment_type_day_to_day_enabled = false
            state.sender_city_id = null
            state.sender_dispatcher_sector_id = null
            state.sender_title = null
            state.sender_additional_services = { ...EMPTY_ADDITIONAL_SERVICES }
            state.is_sender_additional_services_setted = false
            state.is_sender_additional_services_update = false

            state.company_id = null
            state.company_verify = null

            take_time_slot_state.is_enabled = false
            take_time_slot_state.is_completed = false
            take_time_slot_state.sender = null
            take_time_slot_state.timeSlot = null

            delivery_time_slot_state.is_component_showed = false
            delivery_time_slot_state.cargo_index = null
        }

        const { orderPayload } = useCreateOrder()
        const notifications = useNotifications()

        const onSubmitOrder = async () => {
            try {
                state.is_creating_order = true

                const payload = orderPayload(state, take_time_slot_state.timeSlot)
                if (!payload) return

                await orders.createOrder(payload)

                notifications.notify({
                    title: 'Заказ создан',
                    type: 'success',
                })

                state.is_order_created = true
                router.push('/')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании заказа произошла ошбика' })
            } finally {
                state.is_creating_order = false
            }
        }

        const onCreateFromExcel = async (file: File) => {
            if (!state.send_date) {
                notifications.notify({ title: 'Пожалуйста, укажите дату отправки', type: 'danger' })
                return
            }

            if (!state.send_period) {
                notifications.notify({
                    title: 'Пожалуйста, укажите период отправки',
                    type: 'danger',
                })
                return
            }

            try {
                state.is_creating_order = true
                state.is_excel_loading = true

                const company_id = state.company_id as number
                const sender_id = state.sender_id as number

                const payload = new FormData()

                payload.append('company_id', company_id.toString())
                payload.append('title', state.sender_title as string)
                payload.append('sender_id', sender_id.toString())
                payload.append('take_date', state.send_date)
                payload.append('period_id', state.send_period.toString())
                payload.append('file', file)

                await orders.createOrderFromExcel(payload)

                notifications.notify({
                    title: 'Заказ создан',
                    type: 'success',
                })

                state.is_order_created = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании заказа из excel произошла ошибка',
                })
            } finally {
                state.is_excel_loading = false
                state.is_creating_order = false
            }
        }

        const openExcelMultipleModal = () => {
            excel_multiple_state.is_opened = true
        }

        const closeExcelMultipleModal = () => {
            excel_multiple_state.is_opened = false
        }

        const onExcelMultipleOrderCreated = () => {
            closeExcelMultipleModal()
            router.push('/')
        }

        // computeds
        const isCompanySelected = computed(() => Boolean(state.company_id))
        const isBosch = computed(() => state.company_id === BOSCH_ID)
        const isSenderSelected = computed(() => Boolean(state.sender_id) && Boolean(state.sender_city_id))
        const isDateSelected = computed(() => Boolean(state.send_date && state.send_period))
        const isReceiverSelected = computed(() => Boolean(state.receiver))

        const isTakeTimeSlotSelected = computed(() => {
            if (take_time_slot_state.is_enabled) {
                return take_time_slot_state.is_completed
            }

            return Boolean(state.sender_id) && Boolean(state.sender_city_id)
        })

        const selectedReceiversIds = computed(() => {
            return state.completed_cargos.map((completed_cargo) => completed_cargo.receiver.id)
        })

        const cargoForDeliveryTimeSlot = computed(() => {
            if (delivery_time_slot_state.is_component_showed) {
                if (delivery_time_slot_state.cargo_index !== null) {
                    return state.completed_cargos[delivery_time_slot_state.cargo_index]
                }
            }

            return null
        })

        const senderDataTimeForDeliveryTimeSlot = computed(() => {
            if (take_time_slot_state.is_enabled) {
                if (state.send_date) {
                    if (take_time_slot_state.timeSlot) {
                        return `${state.send_date} ${take_time_slot_state.timeSlot.timeTo}:00`
                    }

                    return `${state.send_date} 12:00:00`
                }
            }

            return null
        })

        return {
            state,
            take_time_slot_state,
            delivery_time_slot_state,
            excel_multiple_state,
            // take time slot
            onTakeTimeSlotSubmit,
            onTakeTimeSlotReset,
            // company
            onCompanySubmit,
            onCompanyReset,
            // sender
            onSenderSubmit,
            onSenderReset,
            //
            onSubmitSenderAdditionalServices,
            onStartUpdateSenderAdditionalServices,
            // date
            onDateSubmit,
            onDateReset,
            // receiver
            onReceiverSubmit,
            onReceiverReset,
            // cargo
            onCargoSubmit,
            cancelUpdateCargo,
            onDeleteCargo,
            onStartUpdateCargo,
            // computeds
            isCompanySelected,
            isBosch,
            isSenderSelected,
            isTakeTimeSlotSelected,
            isDateSelected,
            isReceiverSelected,
            selectedReceiversIds,
            // create
            onSubmitOrder,
            resetOrder,
            onUpdateCompletedCargoAdditionalServices,
            onCreateFromExcel,
            //
            LTLHintText,
            //
            openExcelMultipleModal,
            closeExcelMultipleModal,
            onExcelMultipleOrderCreated,
            cargoForDeliveryTimeSlot,
            senderDataTimeForDeliveryTimeSlot,
            onDeliveryTimeSlotSubmit,
        }
    },
})
